import { useMemo } from 'react';
import { cssVar, Divider, HStack, VStack } from '@cad-arc';
import { useFormContext } from 'react-hook-form';
import {
    ActionTypeEnum,
    ElasticUserAction,
    EventPanelFormType,
    EventPanelMetadata,
} from '@mark43/cad-api';
import { LoadingIndicator } from '../../../common/components/loading-indicator';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useGetEventIdAndTabNameFromParams } from '../../hooks/use-get-event-id-and-tab-name-from-params';
import { useEventLocationCautionsQuery } from '../../../event-panel/event-state/use-event-location-cautions-query';
import { FrTabContentWrapper, TOTAL_USED_PAGE_HEIGHT_WITHOUT_TAB_CONTENT_PADDING } from './styled';
import { EVENT_SUMMARY_SECTION_WIDTH } from '../../../fr-event-common-components/constants';
import { FrEventLocationSelectValueDisplay } from '../../../fr-event-common-components/fr-event-location-select-value-display';
import { FrEventNearbyCautions } from '../../../fr-event-common-components/fr-event-nearby-cautions';
import { FREventSummaryEntitiesAndReports } from '../../../fr-event-common-components/fr-event-summary-entities-and-reports';
import { FrEventSummary } from '../../../fr-event-common-components/fr-event-summary';
import { useEventMetadataStore } from '../../../event-panel/event-panel-form/event-metadata';
import { FrEventFormFieldsSummary } from '../../../fr-event-common-components/fr-event-form-fields-summary';
import { useIntegratedEventDetailsQuery } from '../../../common/hooks/use-integrated-event-details-query';
import { FrAssistingAgenciesSection } from '../fr-assisting-agencies-section';
import { FrTimestampsSection } from '../fr-timestamps-section';
import { FrScheduledEventDetailsSection } from '../fr-scheduled-event-details-section';
import { FrDispositionSection } from '../fr-disposition-section';
import { useEventLogQuery } from '../../../event-panel/event-state/use-event-log-query';

export default function FrEventInfoTab() {
    const { eventId } = useGetEventIdAndTabNameFromParams();
    const {
        improvedScheduledEvents,
        additionalDispositionsEnabled,
        rollOutFrFooterAndTabRedesignEnabled,
    } = useEnabledFeatures();
    const { data: cautions, isLoading: areCautionsLoading } = useEventLocationCautionsQuery({
        eventId,
    });
    const eventForm = useFormContext<EventPanelFormType>();
    const { agencyTypeGlobalAttrId, isScheduledEvent } =
        useEventMetadataStore().state.metadata ?? ({} as EventPanelMetadata);
    const {
        data: eventDetailsData,
        isInitialLoading: eventDetailsDataLoading,
    } = useIntegratedEventDetailsQuery({ eventId, enabled: !!eventId });

    const { data } = useEventLogQuery({ eventId });
    const eventDispositionData = useMemo(() => {
        let dispositions: ElasticUserAction | undefined;
        let reactivations: ElasticUserAction | undefined;

        for (const item of data || []) {
            if (item.actionType === ActionTypeEnum.CLEARED_EVENT.name && !dispositions) {
                dispositions = item;
            } else if (
                item.actionType === ActionTypeEnum.EVENT_REACTIVATED.name &&
                !reactivations
            ) {
                reactivations = item;
            }
            if (dispositions && reactivations) break;
        }

        return { dispositions, reactivations };
    }, [data]);

    return (
        <FrTabContentWrapper $noDefaultPadding>
            <HStack w="100%" alignItems="flex-start" data-testid="fr-event-info-tab">
                <VStack
                    w={`calc(100% - ${EVENT_SUMMARY_SECTION_WIDTH})`}
                    h={`calc(100vh - ${TOTAL_USED_PAGE_HEIGHT_WITHOUT_TAB_CONTENT_PADDING}px)`}
                    alignItems="flex-start"
                    p={cssVar('arc.space.6')}
                    overflowY="auto"
                    spacing={4}
                >
                    <FrEventLocationSelectValueDisplay
                        cautions={cautions}
                        areCautionsLoading={areCautionsLoading}
                    />
                    <FrEventNearbyCautions
                        areCautionsLoading={areCautionsLoading}
                        cautions={cautions}
                    />
                    <FrEventFormFieldsSummary
                        eventForm={eventForm}
                        agencyTypeGlobalAttrId={agencyTypeGlobalAttrId}
                    />
                    {(eventDetailsDataLoading || !eventDetailsData) && <LoadingIndicator />}
                    {!eventDetailsDataLoading && !!eventDetailsData && (
                        <>
                            <Divider style={{ marginTop: '16px' }} />
                            {improvedScheduledEvents && isScheduledEvent ? (
                                <FrScheduledEventDetailsSection
                                    receivedDateUtc={eventDetailsData.receivedDateUtc}
                                />
                            ) : (
                                <FrTimestampsSection
                                    receivedDateUtc={eventDetailsData.receivedDateUtc}
                                    initiatedDateTimeUtc={eventDetailsData.initiatedDateTimeUtc}
                                    transmittedDateUtc={eventDetailsData.transmittedDateUtc}
                                    firstDispatchDateUtc={eventDetailsData.firstDispatchDateUtc}
                                    dispatchDateUtc={eventDetailsData.dispatchDateUtc}
                                    arrivalDateUtc={eventDetailsData.arrivalDateUtc}
                                    firstArrivalDateUtc={eventDetailsData.firstArrivalDateUtc}
                                    isScheduled={isScheduledEvent}
                                />
                            )}
                            <Divider style={{ marginTop: '16px' }} />
                            <FrAssistingAgenciesSection
                                assistingAgencies={eventDetailsData.assistingAgencies}
                            />
                            <Divider style={{ marginTop: '16px' }} />
                            <FrDispositionSection
                                dispositions={eventDispositionData.dispositions}
                                reactivations={eventDispositionData.reactivations}
                                additionalDispositionsEnabled={additionalDispositionsEnabled}
                            />
                        </>
                    )}
                </VStack>
                {rollOutFrFooterAndTabRedesignEnabled ? (
                    <FrEventSummary
                        height={`calc(100vh - ${TOTAL_USED_PAGE_HEIGHT_WITHOUT_TAB_CONTENT_PADDING}px)`}
                    />
                ) : (
                    <VStack
                        w={EVENT_SUMMARY_SECTION_WIDTH}
                        h={`calc(100vh - ${TOTAL_USED_PAGE_HEIGHT_WITHOUT_TAB_CONTENT_PADDING}px)`}
                        justifyContent="flex-start"
                        alignItems="center"
                        borderLeftWidth="1px"
                        borderLeftColor="border.default"
                        overflowY="auto"
                        paddingBottom={cssVar('arc.space.6')}
                        style={{ marginLeft: 0 }}
                    >
                        <FREventSummaryEntitiesAndReports
                            customStyles={{
                                padding: `${cssVar('arc.space.6')} ${cssVar('arc.space.6')} 0`,
                            }}
                        />
                    </VStack>
                )}
            </HStack>
        </FrTabContentWrapper>
    );
}
